import React from 'react';
import { FadeIn, CTAButton, Section, SectionTitle, Accent } from './Shared';
import { ProductVideo } from './ProductVideo';
import { featuredTestimonials } from '../data/testimonials';
import { ShieldCheck, Lock, Globe2, Star, X, ArrowRightIcon, FileTextIcon, ZapIcon, LayersIcon, TrophyIcon } from 'lucide-react';
const HERO_MOCKUP = 'https://cdn.magicpatterns.com/uploads/4uX3G3wUEjM6S8x4n4MirU/MOCKUP_PRINCIPAL.png';
const PRODUCT_VIDEO = 'https://cdn.magicpatterns.com/uploads/gU2Fgk8LqeyNecoZp1uU4n/VIDEO%20DE%20CUADERNOS.mp4';
export const Hero = () => {
  const chips = [{
    icon: FileTextIcon,
    label: '100% PDF'
  }, {
    icon: ZapIcon,
    label: 'Access in 5 min'
  }, {
    icon: LayersIcon,
    label: '365 sessions'
  }];
  return <section className="relative overflow-hidden border-b border-white/5 bg-slate-950" data-id="element-291" data-mp="dyhfqgh34m1s">
      {/* Subtle top light — solid, not a gradient */}
      <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-sky-400/40" aria-hidden="true" data-id="element-292" data-mp="1pf7q8bsjk017" />
      {/* minmax(0,1fr) keeps the mobile column exactly the screen width, even
          when content (or a translated label) is wider — nothing gets pushed right. */}
      <div className="mx-auto grid w-full max-w-6xl grid-cols-[minmax(0,1fr)] items-center justify-items-center gap-3 px-5 pb-10 pt-5 sm:gap-6 sm:pt-8 md:px-8 md:pb-20 md:pt-14 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.15fr)] lg:justify-items-stretch lg:gap-x-12 lg:gap-y-8" data-id="element-293" data-mp="189drwvknvhn4">
        {/* Headline — fixed line breaks on mobile, fluid size so every line fits */}
        <FadeIn className="flex w-full min-w-0 flex-col items-center text-center lg:col-start-1 lg:row-start-1 lg:items-start lg:self-end lg:text-left" data-id="element-294" data-mp="tjewh0khqkbc">
          <p className="mb-2.5 flex items-center justify-center gap-2 text-[11px] font-semibold uppercase tracking-widest text-sky-400 sm:mb-4 sm:text-xs" data-id="element-295" data-mp="kkl27gumu60y">
            <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-sky-400" aria-hidden="true" data-id="element-296" data-mp="fn1dfn0b6aur" />
            Functional Fitness 365™
          </p>
          {/* Italic glyphs lean right; a tiny right padding re-centers them optically */}
          <h1 className="w-full break-words pr-[0.06em] font-display text-[clamp(30px,9.6vw,42px)] font-black uppercase italic leading-[0.88] tracking-tight text-white sm:text-5xl sm:leading-[0.92] md:text-6xl lg:pr-0 lg:text-[68px]" data-id="element-297" data-mp="5xvvov1yqsl2">
            <span className="block sm:inline" data-id="element-298" data-mp="3s52u6jek0tv1">Plan your</span>{' '}
            <span className="block sm:inline" data-id="element-299" data-mp="jo7s365kzdi0">entire year of</span>{' '}
            <span className="block sm:inline" data-id="element-300" data-mp="onuea53e042y"><Accent data-id="element-301" data-mp="oo3vp4ad92x1">functional</Accent></span>{' '}
            <span className="block sm:inline" data-id="element-302" data-mp="opwzmbtlh91c">classes.</span>
            <span className="mt-1 block text-slate-400" data-id="element-303" data-mp="9xwib2z7ua37">In 5 minutes.</span>
          </h1>
          <p className="mx-auto mt-3 max-w-[320px] text-[15px] leading-snug text-slate-300 sm:mt-5 sm:max-w-md sm:text-base sm:leading-relaxed md:text-lg lg:mx-0" data-id="element-304" data-mp="ewzf6051la03">
            365 ready-to-teach sessions — whether you're a seasoned coach or
            just getting started.
          </p>
        </FadeIn>

        {/* Mockup */}
        <FadeIn delay={0.1} className="flex w-full min-w-0 justify-center lg:col-start-2 lg:row-span-2 lg:row-start-1" data-id="element-305" data-mp="2s2wcfuxe5n2w">
          <a href="#pricing" className="group block w-full" aria-label="See the offer" data-id="element-306" data-mp="2n3thj3b4kx8n">
            <img src={HERO_MOCKUP} alt="Functional Fitness 365 — the 6 volumes, bonus pack and digital access on tablet, desktop and phone" width={1536} height={1024} loading="eager" decoding="async"
          // @ts-ignore fetchpriority is valid HTML
          fetchpriority="high" className="mx-auto block h-auto w-full max-w-[400px] object-contain sm:max-w-[560px] transition-transform duration-200 ease-out group-hover:-translate-y-1 lg:max-w-none" data-id="element-307" data-mp="31wmx0oqedddb" />
          </a>
        </FadeIn>

        {/* CTA */}
        <FadeIn delay={0.15} className="flex w-full min-w-0 max-w-[420px] flex-col items-center sm:max-w-none lg:col-start-1 lg:row-start-2 lg:items-start lg:self-start" data-id="element-308" data-mp="3pk4tgxklwzmm">
          <ul className="mb-4 flex w-full flex-wrap items-center justify-center gap-1.5 sm:mb-5 sm:gap-2 lg:justify-start" data-id="element-309" data-mp="oqqcx3jrwcys">
            {chips.map(({
            icon: Icon,
            label
          }) => <li key={label} className="inline-flex items-center gap-1 whitespace-nowrap rounded-full border border-white/10 bg-white/5 px-2.5 py-1 text-[11px] font-medium text-slate-200 sm:gap-1.5 sm:px-3 sm:py-1.5 sm:text-xs md:text-sm" data-id="element-310" data-mp="xotslj82ufio">
                <Icon size={14} className="text-sky-400" aria-hidden="true" data-id="element-311" data-mp="1bobg7v9mzmqg" />
                {label}
              </li>)}
          </ul>
          <CTAButton href="#pricing" className="w-full sm:w-auto" data-id="element-312" data-mp="1gifoqiyjw2tn">
            Get Functional Fitness 365
            <ArrowRightIcon size={18} className="transition-transform duration-200 group-hover/cta:translate-x-0.5" aria-hidden="true" data-id="element-313" data-mp="1qe99gpmfb2mi" />
          </CTAButton>
          <div className="mt-3 flex w-full flex-wrap items-center justify-center gap-x-3 gap-y-1 text-center text-[11px] text-slate-400 sm:mt-4 sm:gap-x-4 sm:gap-y-1.5 sm:text-xs lg:justify-start" data-id="element-314" data-mp="1vdviemv7xwpi">
            <span className="flex items-center gap-1.5" data-id="element-315" data-mp="3auqiuu20dsbz">
              <ShieldCheck size={14} className="text-emerald-400" aria-hidden="true" data-id="element-316" data-mp="35wp06a8hvv11" /> 7-day guarantee
            </span>
            <span className="flex items-center gap-1.5" data-id="element-317" data-mp="30z6yn4wt4i32">
              <Lock size={14} aria-hidden="true" data-id="element-318" data-mp="2gi5gt82584e" /> Secure checkout
            </span>
            <span className="flex items-center gap-1.5" data-id="element-319" data-mp="2ichtj59jrwv">
              <Globe2 size={14} className="text-sky-400" aria-hidden="true" data-id="element-320" data-mp="3to458arnoqfz" /> Pay in your local currency
            </span>
          </div>
        </FadeIn>
      </div>
    </section>;
};
export const SocialProof = () => {
  const stats = [{
    value: '+500',
    label: 'coaches trust it'
  }, {
    value: '6',
    label: 'ready volumes'
  }, {
    value: '5',
    label: 'bonuses included'
  }, {
    value: '4.9/5',
    label: 'average rating',
    star: true
  }];
  return <div className="border-b border-white/5 bg-slate-900/40 px-5 py-8 md:px-8" data-id="element-321" data-mp="2ao4txalrem34">
      <dl className="mx-auto grid max-w-6xl grid-cols-2 gap-y-6 md:grid-cols-4" data-id="element-322" data-mp="2aq7tal8sxrki">
        {stats.map((s, i) => <FadeIn key={s.label} delay={Math.min(i * 0.05, 0.2)} className={`text-center ${i % 2 === 1 ? 'border-l border-white/10' : ''} ${i > 0 ? 'md:border-l md:border-white/10' : ''}`} data-id="element-323" data-mp="20ybjicxcsd0g" data-mpi={i}>
            <dt className="sr-only" data-id="element-324" data-mp="25v21x6wmrkp1">{s.label}</dt>
            <dd className="font-display text-4xl font-black italic leading-none tracking-tight text-white md:text-5xl" data-id="element-325" data-mp="1rwke796q6874">
              {s.value}
            </dd>
            <dd className="mt-2 flex items-center justify-center gap-1 text-sm text-slate-400" data-id="element-326" data-mp="1hzfurh49kqu0">
              {s.label}
              {s.star && <Star size={12} className="fill-amber-400 text-amber-400" aria-hidden="true" data-id="element-327" data-mp="1mzlnl0m9sjdo" />}
            </dd>
          </FadeIn>)}
      </dl>
    </div>;
};
const Stars = ({
  size = 12
}: {
  size?: number;
}) => <div className="flex text-amber-400" role="img" aria-label="5 out of 5 stars" data-id="element-328" data-mp="3hb8pf73kxk3k">
    {[...Array(5)].map((_, j) => <Star key={j} size={size} className="fill-current" aria-hidden="true" data-id="element-329" data-mp="15phua9nzpx6c" data-mpi={j} />)}
  </div>;
export const Testimonials1 = () => <Section className="bg-slate-950 !py-12 md:!py-16" data-id="element-330" data-mp="1ferj9gnjezq8">
    <div className="grid grid-cols-1 gap-4 md:grid-cols-3" data-id="element-331" data-mp="1fgkr2z9vzpzt">
      {featuredTestimonials.map((t, i) => <FadeIn key={t.author} delay={Math.min(i * 0.05, 0.2)} data-id="element-332" data-mp="1an04e3dquifx" data-mpi={i}>
          <figure className="flex h-full flex-col rounded-2xl border border-white/10 bg-white/[0.03] p-5 md:p-6" data-id="element-333" data-mp="1oeodrywlyst7">
            <Stars data-id="element-334" data-mp="1jftlm6cr3rwf" />
            <blockquote className="mt-3 text-[15px] leading-relaxed text-slate-200" data-id="element-335" data-mp="1jgd0tfb0o26r">
              “{t.text}”
            </blockquote>
            <figcaption className="mt-auto flex items-center gap-3 pt-5" data-id="element-336" data-mp="1tf2curg8esf3">
              <div className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full ${t.bg} text-xs font-bold text-white`} data-id="element-337" data-mp="1tcy9qbq5pxfv">
                {t.initials}
              </div>
              <div className="min-w-0" data-id="element-338" data-mp="3v8080txhdx37">
                <p className="text-sm font-semibold leading-tight text-white" data-id="element-339" data-mp="39d9oshll4003">{t.author}</p>
                <p className="text-xs text-slate-500" data-id="element-340" data-mp="3ec32ob4s9t0s">{t.role}</p>
              </div>
            </figcaption>
          </figure>
        </FadeIn>)}
    </div>
  </Section>;
export { Stars };
export const Problem = () => {
  const painPoints = ["You walk into the gym not knowing exactly what class you're teaching today", 'You spend hours hunting for exercises that fit your group', "You repeat the same circuits because you don't have a session library", "A new member shows up and you don't know how to adapt on the spot", "You want a yearly program but have no time to build one", 'Your group plateaus every time you try to give them real progression', 'Weekends searching for ideas. Mondays improvising. Same cycle.'];
  return <Section className="bg-slate-900/40" data-id="element-341" data-mp="flt5f322udf4">
      <FadeIn data-id="element-342" data-mp="jn50vp7mtsv8">
        <SectionTitle data-id="element-343" data-mp="jmmwu6eqaacr">
          If you coach <Accent data-id="element-344" data-mp="jlv3714oymp4">functional fitness</Accent>,
          <br className="hidden md:block" data-id="element-345" data-mp="2468gnpydgs1c" /> this will sound familiar…
        </SectionTitle>
      </FadeIn>

      <ul className="mx-auto mb-16 grid max-w-4xl grid-cols-1 gap-x-8 gap-y-1 md:mb-24 md:grid-cols-2" data-id="element-346" data-mp="203vch5z9x26o">
        {painPoints.map((point, i) => <li key={point} className={`flex items-start gap-3 border-b border-white/5 py-3.5 ${i === painPoints.length - 1 ? 'md:col-span-2 md:mx-auto md:max-w-md md:border-b-0' : ''}`} data-id="element-347" data-mp="1vbam6efkt77w" data-mpi={i}>
            <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-red-500/15" data-id="element-348" data-mp="1lgsdeuvaylh8">
              <X size={12} className="text-red-400" strokeWidth={3} aria-hidden="true" data-id="element-349" data-mp="1gi8u7283fnsk" />
            </span>
            <p className="text-[15px] leading-normal text-slate-300 md:text-base" data-id="element-350" data-mp="1cgds3vic96dq">{point}</p>
          </li>)}
      </ul>

      <FadeIn className="mx-auto mb-12 grid max-w-5xl items-center gap-6 md:mb-16 md:grid-cols-2 md:gap-12" data-id="element-351" data-mp="36t3ocjw6iroa">
        <h3 className="text-center font-display text-4xl font-black uppercase italic leading-[0.92] tracking-tight text-white md:text-left md:text-6xl" data-id="element-352" data-mp="31t7kqlre9299">
          Still <Accent data-id="element-353" data-mp="idhlkgets8d9">improvising</Accent> your workouts?
        </h3>
        <div className="space-y-4 text-base leading-relaxed text-slate-300 md:text-lg" data-id="element-354" data-mp="dc1y6hp6dprp">
          <p data-id="element-355" data-mp="rb1mtpcou4nv">
            You chose this profession because you love functional training and
            watching your group grow.
          </p>
          <p data-id="element-356" data-mp="mbfb5kcicwh6">
            Whether you're a seasoned coach or just starting out, the problem
            is the same: you spend more time <strong className="font-semibold text-white" data-id="element-357" data-mp="3qrzvknobtxzi">SEARCHING</strong> for what to
            teach than actually <strong className="font-semibold text-white" data-id="element-358" data-mp="8dqn63n5997w">TEACHING</strong>. And while you lose your
            Sundays building sessions, your competitors keep moving ahead.
          </p>
        </div>
      </FadeIn>

      <FadeIn className="mx-auto max-w-3xl" data-id="element-359" data-mp="3c1inilh6s9tn">
        <div className="rounded-3xl border border-sky-400/40 bg-blue-600/10 px-6 py-8 text-center md:px-10 md:py-10" data-id="element-360" data-mp="3c07iyjfx2y1h">
          <p className="font-display text-2xl font-black uppercase italic leading-[1.02] tracking-tight text-white md:text-4xl" data-id="element-361" data-mp="1mlomzk3p5qvs">
            What if this was the <Accent data-id="element-362" data-mp="1rdpvqw1nozh2">last class</Accent>
            <br data-id="element-363" data-mp="1dkqlb8gqdvgd" /> you ever prepared this way?
          </p>
        </div>
      </FadeIn>

      <FadeIn className="mt-20 text-center md:mt-28" data-id="element-364" data-mp="1dixb5j6wst3m">
        <p className="mb-5 inline-flex items-center gap-2 rounded-full bg-amber-400 px-3.5 py-1.5 text-xs font-bold uppercase tracking-wide text-amber-950" data-id="element-365" data-mp="1ikmnr39s5ett">
          <TrophyIcon size={14} aria-hidden="true" data-id="element-366" data-mp="2aar3upqibfp8" />
          The #1 system for functional fitness coaches
        </p>
        <h2 className="font-display text-4xl font-black uppercase italic leading-[0.95] tracking-tight text-white md:text-6xl" data-id="element-367" data-mp="1wc16ra3otg0v">
          Functional Fitness <Accent data-id="element-368" data-mp="218hxmc29u8hn">365™</Accent>
        </h2>
        <p className="mx-auto mt-4 max-w-xl text-base leading-relaxed text-slate-300 md:text-lg" data-id="element-369" data-mp="kvjme8dd73ul">
          6 Volumes + 5 Bonuses — 365 ready sessions · A full year · Everything
          solved.
        </p>

        <div className="relative mx-auto mt-10 max-w-3xl overflow-hidden rounded-3xl border border-white/10 bg-slate-900 shadow-2xl shadow-black/50" data-id="element-370" data-mp="5tb2xnpvoc6r">
          <ProductVideo src={PRODUCT_VIDEO} label="Preview of the Functional Fitness 365 workbooks" className="block w-full object-cover" data-id="element-371" data-mp="1tkgfw7dwi81" />
        </div>
      </FadeIn>
    </Section>;
};